import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Line,
  ComposedChart,
} from 'recharts'

function formatCurrency(value) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `$${Math.round(value / 1000)}K`
  return `$${value}`
}

/**
 * Renders overlaid exceedance curves for current state + investment tiers.
 *
 * @param {Array} currentCurve - Current exceedance curve [{threshold, probability}]
 * @param {Object} tierResults - { light: compareBeforeAfter result, moderate: ..., aggressive: ... }
 * @param {Array} scenarios - Loss scenario results for building tier curves
 * @param {Function} simulateFn - simulateAggregateExposure function
 * @param {Object} tierCalibrations - { light: calibration, moderate: ..., aggressive: ... }
 * @param {Object} impactData - Impact data for insurance offset
 */
function InvestmentComparisonChart({ currentCurve, tierResults }) {
  if (!currentCurve || currentCurve.length === 0) return null

  // Build merged dataset: each threshold has current + tier probabilities
  // Use the current curve's thresholds as the base
  const data = currentCurve.map(point => {
    const row = {
      threshold: point.threshold,
      current: Math.round(point.probability * 100 * 10) / 10
    }

    // For each tier, interpolate probability at this threshold from the tier's P90/P50/etc
    // We use the ratio approach: tier's improved ALE / current ALE scales the curve
    if (tierResults) {
      for (const [tierId, result] of Object.entries(tierResults)) {
        if (!result?.current?.aggregateALE || !result?.improved?.aggregateALE) continue
        // Scale: the improved distribution is compressed relative to current
        const scaleFactor = result.improved.aggregateALE / Math.max(result.current.aggregateALE, 1)
        // At a given threshold, the improved curve's probability is approximately
        // the current curve's probability at threshold/scaleFactor
        const effectiveThreshold = point.threshold / Math.max(scaleFactor, 0.1)
        // Interpolate from current curve
        let interpProb = 0
        for (let i = 0; i < currentCurve.length - 1; i++) {
          if (currentCurve[i].threshold <= effectiveThreshold && currentCurve[i + 1].threshold >= effectiveThreshold) {
            const ratio = (effectiveThreshold - currentCurve[i].threshold) / (currentCurve[i + 1].threshold - currentCurve[i].threshold)
            interpProb = currentCurve[i].probability - (currentCurve[i].probability - currentCurve[i + 1].probability) * ratio
            break
          }
          if (effectiveThreshold > currentCurve[currentCurve.length - 1].threshold) {
            interpProb = 0
          }
          if (effectiveThreshold <= 0) {
            interpProb = currentCurve[0].probability
          }
        }
        row[tierId] = Math.max(0, Math.round(interpProb * 100 * 10) / 10)
      }
    }

    return row
  })

  const TIER_COLORS = {
    light: '#F59E0B',
    moderate: '#3B82F6',
    aggressive: '#22C55E'
  }

  const TIER_LABELS = {
    light: 'Light',
    moderate: 'Moderate',
    aggressive: 'Aggressive'
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow-lg text-xs">
        <p className="text-gray-900 dark:text-white font-medium mb-1">{formatCurrency(label)}</p>
        {payload.map(p => (
          <p key={p.dataKey} style={{ color: p.color }}>
            {p.dataKey === 'current' ? 'Current' : TIER_LABELS[p.dataKey] || p.dataKey}: {p.value}%
          </p>
        ))}
      </div>
    )
  }

  const tiers = tierResults ? Object.keys(tierResults).filter(t => tierResults[t]) : []

  return (
    <div className="relative">
      {/* Legend — top right corner */}
      <div className="absolute top-8 right-12 z-10 bg-gray-50/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-gray-700/40 shadow-sm px-3 py-2 space-y-1">
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-red-500 rounded" />
          <span className="text-[10px] text-gray-600 dark:text-gray-300 font-medium">Current</span>
        </div>
        {tiers.map(tierId => (
          <div key={tierId} className="flex items-center gap-2">
            <div className="w-3 h-0.5 rounded" style={{ backgroundColor: TIER_COLORS[tierId] }} />
            <span className="text-[10px] font-medium" style={{ color: TIER_COLORS[tierId] }}>
              {TIER_LABELS[tierId]}
              {tierResults[tierId]?.delta?.aleReductionPercent != null && (
                <span className="text-gray-400 dark:text-gray-500 font-normal ml-1">({tierResults[tierId].delta.aleReductionPercent}%)</span>
              )}
            </span>
          </div>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={data} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" opacity={0.5} />
          <XAxis
            dataKey="threshold"
            tickFormatter={formatCurrency}
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
            axisLine={{ stroke: '#D1D5DB' }}
            tickLine={{ stroke: '#D1D5DB' }}
            type="number"
          />
          <YAxis
            domain={[0, 100]}
            tickFormatter={(v) => `${v}%`}
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
            axisLine={{ stroke: '#D1D5DB' }}
            tickLine={{ stroke: '#D1D5DB' }}
            width={45}
          />
          <Tooltip content={<CustomTooltip />} />

          {/* Current state - filled area */}
          <Area
            type="monotone"
            dataKey="current"
            stroke="#EF4444"
            strokeWidth={2}
            fill="#EF4444"
            fillOpacity={0.08}
            dot={false}
            name="Current"
          />

          {/* Tier curves - lines only */}
          {tiers.map(tierId => (
            <Line
              key={tierId}
              type="monotone"
              dataKey={tierId}
              stroke={TIER_COLORS[tierId]}
              strokeWidth={2}
              strokeDasharray={tierId === 'light' ? '6 3' : tierId === 'moderate' ? '4 4' : undefined}
              dot={false}
              name={TIER_LABELS[tierId]}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>

    </div>
  )
}

export default InvestmentComparisonChart
